import React, { useMemo, useState, useEffect } from 'react';
import { ModalConfirm as Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Box,
  Typography,
  SimpleCheckbox as Checkbox,
  Input,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { MoneyIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { SingleSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { FormattedMessage } from 'react-intl';
import messages from './messages';

type Props = {
  isOpen: boolean,
  closeModal: () => void,
  confirm: () => void,
  propertyName: string,
  collectionsAgency?: string,
  minimumAmount?: number,
  collectionsAgencyOptions?: Array<{
    value: string,
    text: string,
    disabled?: boolean,
    tooltip?: string,
  }>,
  onCollectionsAgencyChange?: (value: string) => void,
  onMinimumAmountChange?: (amount: number) => void,
  minimumCollectionsAmount?: number,
};

const CollectionsModal = ({
  isOpen,
  closeModal,
  confirm,
  propertyName,
  collectionsAgency = '',
  minimumAmount = 100,
  collectionsAgencyOptions = [],
  onCollectionsAgencyChange,
  onMinimumAmountChange,
  minimumCollectionsAmount = 50,
}: Props) => {
  const [checkedOptions, setCheckedOptions] = useState([]);
  const [localCollectionsAgency, setLocalCollectionsAgency] =
    useState(collectionsAgency);
  const [localMinimumAmount, setLocalMinimumAmount] = useState(minimumAmount);

  // Update local state when props change
  useEffect(() => {
    setLocalCollectionsAgency(collectionsAgency);
    setLocalMinimumAmount(minimumAmount);
  }, [collectionsAgency, minimumAmount]);

  // Reset and initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      setCheckedOptions([]);
      setLocalCollectionsAgency(collectionsAgency);
      setLocalMinimumAmount(minimumAmount);
    }
  }, [isOpen, collectionsAgency, minimumAmount]);

  // Checkbox options with FormattedMessage components
  const options = useMemo(
    () => [
      {
        id: 1,
        textContent: (
          <FormattedMessage
            {...messages.modalContractSigned}
            values={{ propertyName }}
          />
        ),
      },
      {
        id: 2,
        textContent: <FormattedMessage {...messages.modalVerifiedStatus} />,
      },
      {
        id: 3,
        textContent: <FormattedMessage {...messages.modalAuthorizeSharing} />,
      },
    ],
    [propertyName],
  );

  const reset = () => {
    setCheckedOptions([]);
    setLocalCollectionsAgency(collectionsAgency);
    setLocalMinimumAmount(minimumAmount);
    closeModal();
  };

  const handleCheckboxChange = (changedOption) => {
    const checkedOptionsIds = checkedOptions.map((co) => co.id);
    if (checkedOptionsIds.includes(changedOption.id)) {
      setCheckedOptions(
        checkedOptions.filter((co) => co.id !== changedOption.id),
      );
    } else {
      setCheckedOptions([...checkedOptions, changedOption]);
    }
  };

  const handleCancel = () => {
    reset();
  };

  // Form validation - all checkboxes must be checked, agency selected, valid amount
  const isFormValid = () => {
    const allCheckboxesSelected = checkedOptions.length === options.length;
    const hasValidAgency =
      localCollectionsAgency && localCollectionsAgency !== '';
    const hasValidAmount = localMinimumAmount >= minimumCollectionsAmount;

    return allCheckboxesSelected && hasValidAgency && hasValidAmount;
  };

  const handleConfirm = () => {
    if (isFormValid()) {
      confirm();
      setCheckedOptions([]);
    }
  };

  // Handle collections agency selection
  const handleAgencyChange = (value) => {
    setLocalCollectionsAgency(value);
    // Notify parent component immediately
    if (onCollectionsAgencyChange) {
      onCollectionsAgencyChange(value);
    }
  };

  // Handle minimum amount changes
  const handleAmountChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setLocalMinimumAmount(value);
    // Notify parent component immediately
    if (onMinimumAmountChange) {
      onMinimumAmountChange(value);
    }
  };

  // Modal action buttons
  const actionsProps = [
    {
      variant: 'text',
      onClick: handleCancel,
      children: <FormattedMessage {...messages.modalCancel} />,
    },
    {
      variant: 'primary',
      onClick: handleConfirm,
      disabled: !isFormValid(),
      children: <FormattedMessage {...messages.modalSaveChanges} />,
    },
  ];

  return (
    <Modal
      open={isOpen}
      actionsProps={actionsProps}
      onClose={reset}
      title={<FormattedMessage {...messages.modalTitle} />}
    >
      <Box sx={{ width: 480 }}>
        {/* Collections Agency Selection */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="bodyMedium"
            sx={{ mb: 1, fontWeight: 500 }}
            display="block"
          >
            <FormattedMessage {...messages.collectionsAgency} />
          </Typography>
          <SingleSelect
            sx={{ width: '100%' }}
            options={collectionsAgencyOptions}
            value={localCollectionsAgency}
            onChange={handleAgencyChange}
          />
        </Box>

        {/* Minimum Account Balance Input */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="bodyMedium"
            sx={{ mb: 1, fontWeight: 500 }}
            display="block"
          >
            <FormattedMessage {...messages.minAmount} />
          </Typography>
          <Input
            value={localMinimumAmount}
            onChange={handleAmountChange}
            type="number"
            step="0.01"
            min="0"
            startAdornment={<MoneyIcon />}
            sx={{ width: '100%' }}
          />
        </Box>

        {/* Certification Header */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="bodyMedium">
            <FormattedMessage {...messages.modalUnderstandAndCertify} />
          </Typography>
        </Box>

        {/* Certification Checkboxes */}
        {options.map((option) => {
          const isChecked = checkedOptions.some((co) => co.id === option.id);
          return (
            <Box
              key={option.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 2,
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={() => handleCheckboxChange(option)}
              />
              <Typography variant="bodyMedium">{option.textContent}</Typography>
            </Box>
          );
        })}
      </Box>
    </Modal>
  );
};

export default CollectionsModal;
